import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Title, useNotify } from 'react-admin';
import { Button, Card, CardContent, CircularProgress } from '@mui/material';
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import constants from '../../constants';
import { PRIZE_CATEGORIES } from './prizeCategories';

// GH-645: admin drag & drop to control (1) the order of categories and (2) the order
// of prizes within each category. AIProgramPrize is NOT involved. Persists via:
//   PUT /setting/{id}  { value: [slugs] }   (category order — mirrors boostsTemplate)
//   PUT /prize/reorder { orderedIds: [] }    (prize order — sets Prize.order)

const GENERAL_ID = '__general__';
const catLabel = (id: string) =>
	id === GENERAL_ID ? 'ללא קטגוריה' : PRIZE_CATEGORIES.find((c) => c.id === id)?.name ?? id;

type Prize = {
	_id: string;
	title?: string | { he?: string; en?: string };
	category?: string;
	order?: number;
	imageUrl?: string;
};

// title may be a plain string or a localized { he, en } object.
const prizeTitle = (p: Prize) =>
	typeof p.title === 'string' ? p.title : p.title?.he || p.title?.en || p._id;

function SortableItem({ id, children, style: extra }: { id: string; children: any; style?: any }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	return (
		<div
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.6 : 1,
				cursor: 'grab',
				userSelect: 'none',
				...extra,
			}}
		>
			{children}
		</div>
	);
}

const rowStyle = {
	display: 'flex',
	alignItems: 'center',
	gap: 10,
	padding: '10px 14px',
	marginBottom: 8,
	background: '#fff',
	border: '1px solid #e3e6ee',
	borderRadius: 10,
	direction: 'rtl' as const,
};

export default function PrizeOrdering() {
	const notify = useNotify();
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const [loading, setLoading] = useState(true);
	const [catOrder, setCatOrder] = useState<string[]>([]);
	const [catSettingId, setCatSettingId] = useState<string | null>(null);
	const [groups, setGroups] = useState<Record<string, Prize[]>>({});
	const [savingCats, setSavingCats] = useState(false);
	const [savingPrizes, setSavingPrizes] = useState(false);

	const load = async () => {
		setLoading(true);
		try {
			const [pRes, sRes] = await Promise.all([
				axios.get(`${constants.API_URL}/prize?limit=999`),
				axios.get(`${constants.API_URL}/setting/prizeCategoryOrder`).catch(() => null),
			]);
			const list: Prize[] = (pRes.data?.data ?? pRes.data ?? []) as Prize[];
			const order: string[] =
				(sRes && (sRes.data?.value as string[])) ?? PRIZE_CATEGORIES.map((c) => c.id);
			setCatOrder(order);
			setCatSettingId(sRes?.data?._id ?? null);

			const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
			const g: Record<string, Prize[]> = {};
			[...order, GENERAL_ID].forEach((cid) => (g[cid] = []));
			sorted.forEach((p) => {
				const cid = p.category && g[p.category] ? p.category : GENERAL_ID;
				(g[cid] = g[cid] || []).push(p);
			});
			setGroups(g);
		} catch (e: any) {
			notify(`שגיאה בטעינה: ${e?.message || e}`, { type: 'error' });
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		load();
	}, []);

	const onCatDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		setCatOrder((prev) => arrayMove(prev, prev.indexOf(String(active.id)), prev.indexOf(String(over.id))));
	};

	const onPrizeDragEnd = (cid: string) => ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		setGroups((prev) => {
			const arr = prev[cid] || [];
			const from = arr.findIndex((p) => p._id === active.id);
			const to = arr.findIndex((p) => p._id === over.id);
			if (from < 0 || to < 0) return prev;
			return { ...prev, [cid]: arrayMove(arr, from, to) };
		});
	};

	const saveCategories = async () => {
		if (!catSettingId) {
			notify('הגדרת סדר הקטגוריות חסרה במסד — צור אותה קודם', { type: 'warning' });
			return;
		}
		setSavingCats(true);
		try {
			await axios.put(`${constants.API_URL}/setting/${catSettingId}`, { value: catOrder });
			notify('סדר הקטגוריות נשמר', { type: 'success' });
		} catch (e: any) {
			notify(`שגיאה בשמירה: ${e?.message || e}`, { type: 'error' });
		} finally {
			setSavingCats(false);
		}
	};

	const savePrizes = async () => {
		setSavingPrizes(true);
		try {
			const orderedIds = [...catOrder, GENERAL_ID].flatMap((cid) => (groups[cid] || []).map((p) => p._id));
			await axios.put(`${constants.API_URL}/prize/reorder`, { orderedIds });
			notify('סדר הפרסים נשמר', { type: 'success' });
		} catch (e: any) {
			notify(`שגיאה בשמירה: ${e?.message || e}`, { type: 'error' });
		} finally {
			setSavingPrizes(false);
		}
	};

	const orderedCats = useMemo(() => [...catOrder, GENERAL_ID], [catOrder]);

	if (loading) {
		return (
			<div style={{ padding: 40, textAlign: 'center' }}>
				<Title title="סידור פרסים" />
				<CircularProgress />
			</div>
		);
	}

	return (
		<div style={{ padding: 20, maxWidth: 760, direction: 'rtl' }}>
			<Title title="סידור פרסים" />

			{/* ---- category order ---- */}
			<Card style={{ marginBottom: 24 }}>
				<CardContent>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
						<h3 style={{ margin: 0 }}>סדר הקטגוריות</h3>
						<Button variant="contained" onClick={saveCategories} disabled={savingCats}>
							{savingCats ? 'שומר…' : 'שמירת סדר הקטגוריות'}
						</Button>
					</div>
					<p style={{ color: '#6b7280', marginTop: 0 }}>גררו כדי לשנות את סדר הופעת הקטגוריות בעמוד הפרסים.</p>
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCatDragEnd}>
						<SortableContext items={catOrder} strategy={verticalListSortingStrategy}>
							{catOrder.map((cid, i) => (
								<SortableItem key={cid} id={cid}>
									<div style={rowStyle}>
										<span style={{ color: '#9aa0b4', width: 20 }}>{i + 1}</span>
										<span style={{ fontWeight: 600 }}>{catLabel(cid)}</span>
										<span style={{ marginInlineStart: 'auto', color: '#c3c8d6' }}>⠿</span>
									</div>
								</SortableItem>
							))}
						</SortableContext>
					</DndContext>
				</CardContent>
			</Card>

			{/* ---- prizes per category ---- */}
			<Card>
				<CardContent>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
						<h3 style={{ margin: 0 }}>סדר הפרסים בכל קטגוריה</h3>
						<Button variant="contained" onClick={savePrizes} disabled={savingPrizes}>
							{savingPrizes ? 'שומר…' : 'שמירת סדר הפרסים'}
						</Button>
					</div>
					<p style={{ color: '#6b7280', marginTop: 0 }}>גררו פרסים בתוך כל קטגוריה כדי לקבוע את סדר הופעתם.</p>

					{orderedCats.map((cid) => {
						const arr = groups[cid] || [];
						if (arr.length === 0) return null;
						return (
							<div key={cid} style={{ marginBottom: 22 }}>
								<h4 style={{ margin: '0 0 8px', color: '#4f46e5' }}>{catLabel(cid)}</h4>
								<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPrizeDragEnd(cid)}>
									<SortableContext items={arr.map((p) => p._id)} strategy={verticalListSortingStrategy}>
										{arr.map((p) => (
											<SortableItem key={p._id} id={p._id}>
												<div style={rowStyle}>
													{p.imageUrl ? (
														<img src={p.imageUrl} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover' }} />
													) : (
														<div style={{ width: 34, height: 34, borderRadius: 6, background: '#eee' }} />
													)}
													<span>{prizeTitle(p)}</span>
													<span style={{ marginInlineStart: 'auto', color: '#c3c8d6' }}>⠿</span>
												</div>
											</SortableItem>
										))}
									</SortableContext>
								</DndContext>
							</div>
						);
					})}
				</CardContent>
			</Card>
		</div>
	);
}
